<template>
  <div class="gpx-tool">
    <!-- Drop zone / file picker -->
    <label
      class="gpx-dropzone"
      :class="{ 'gpx-dropzone--active': dragging, 'gpx-dropzone--error': phase === 'error' }"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".gpx,application/gpx+xml,application/xml,text/xml"
        class="gpx-dropzone__input"
        @change="onFileChange"
      />
      <i class="material-icons gpx-dropzone__icon">upload_file</i>
      <p class="gpx-dropzone__title mb-1">
        <strong>Drop a GPX file here</strong> or click to choose
      </p>
      <p class="gpx-dropzone__hint text-muted mb-0">
        Your file is processed entirely in your browser — it is never uploaded to our servers.
      </p>
      <p v-if="fileName" class="gpx-dropzone__file mt-2 mb-0">
        <i class="material-icons align-middle">description</i> {{ fileName }}
      </p>
    </label>

    <!-- Error -->
    <div v-if="phase === 'error'" class="alert alert-danger mt-3 mb-0" role="alert">
      <strong>Couldn't convert this file.</strong> {{ errorMessage }}
    </div>

    <!-- Result -->
    <div v-if="phase === 'done' && result" class="gpx-result mt-4">
      <div class="gpx-result__summary card">
        <div class="card-body">
          <h2 class="h5 mb-3">
            <i class="material-icons align-middle text-success">check_circle</i>
            Ready to download
          </h2>
          <p class="mb-3">{{ summaryText }}</p>

          <!-- Output type toggle -->
          <div class="mb-3">
            <span class="d-block small text-muted mb-1">Save as</span>
            <div class="btn-group" role="group" :aria-label="$t('garmin_gpx.converter.output_type')">
              <button
                type="button"
                class="btn"
                :class="outputType === 'trk' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="setOutputType('trk')"
              >
                {{ $t('garmin_gpx.converter.track_gpx') }}
              </button>
              <button
                type="button"
                class="btn"
                :class="outputType === 'rte' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="setOutputType('rte')"
              >
                {{ $t('garmin_gpx.converter.route_gpx') }}
              </button>
            </div>
            <i18n-t keypath="garmin_gpx.most_garmin_watches_and_edge_devices" tag="p" class="form-text gpx-help mb-0" scope="global">
              <template #track><strong>{{ $t('garmin_gpx.track') }}</strong></template>
              <template #route><strong>{{ $t('garmin_gpx.route') }}</strong></template>
            </i18n-t>
          </div>

          <button type="button" class="btn btn-success btn-lg" @click="download">
            <i class="material-icons align-middle me-1">download</i>
            Download merged GPX
          </button>
          <button type="button" class="btn btn-link" @click="reset">Convert another file</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { convertGpx, GpxParseError, type ConvertResult, type GpxOutputType } from './mergeGpx';
import { i18n } from '../../i18n';

const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

type Phase = 'idle' | 'done' | 'error';

const phase = ref<Phase>('idle');
const dragging = ref(false);
const errorMessage = ref('');
const fileName = ref('');
const fileInput = ref<HTMLInputElement | null>(null);

// Raw text of the loaded file is kept so toggling the output type re-runs the
// conversion without asking the user to pick the file again.
const sourceText = ref('');
const sourceBaseName = ref('converted');
const outputType = ref<GpxOutputType>('trk');
const result = ref<ConvertResult | null>(null);

const summaryText = computed(() => {
  const r = result.value;
  if (!r) return '';
  const tracks = r.sourceTrackCount === 1
    ? t('garmin_gpx.converter.one_track')
    : t('garmin_gpx.converter.count_tracks', { count: r.sourceTrackCount });
  const routes = r.sourceRouteCount === 1
    ? t('garmin_gpx.converter.one_route')
    : t('garmin_gpx.converter.count_routes', { count: r.sourceRouteCount });
  let merged: string;
  if (r.sourceTrackCount > 0 && r.sourceRouteCount > 0) {
    merged = t('garmin_gpx.converter.tracks_and_routes', { tracks, routes });
  } else if (r.sourceTrackCount > 0) {
    merged = tracks;
  } else if (r.sourceRouteCount > 0) {
    merged = routes;
  } else {
    merged = t('garmin_gpx.converter.your_file');
  }
  const points = r.mergedPointCount;
  const isTrack = outputType.value === 'trk';
  if (r.waypointCount > 0) {
    const waypoints = r.waypointCount === 1
      ? t('garmin_gpx.converter.one_waypoint')
      : t('garmin_gpx.converter.count_waypoints', { count: r.waypointCount });
    return isTrack
      ? t('garmin_gpx.converter.summary_track_with_waypoints', { merged, points, waypoints })
      : t('garmin_gpx.converter.summary_route_with_waypoints', { merged, points, waypoints });
  }
  return isTrack
    ? t('garmin_gpx.converter.summary_track', { merged, points })
    : t('garmin_gpx.converter.summary_route', { merged, points });
});

const runConversion = (): void => {
  try {
    result.value = convertGpx(sourceText.value, { outputType: outputType.value });
    phase.value = 'done';
    errorMessage.value = '';
  } catch (err) {
    result.value = null;
    phase.value = 'error';
    errorMessage.value =
      err instanceof GpxParseError ? err.message : t('garmin_gpx.converter.could_not_read_valid_gpx');
  }
};

const loadFile = (file: File): void => {
  fileName.value = file.name;
  sourceBaseName.value = file.name.replace(/\.gpx$/i, '') || 'converted';
  const reader = new FileReader();
  reader.onload = () => {
    sourceText.value = String(reader.result ?? '');
    // Default output type follows the source (handled inside convertGpx when
    // outputType is omitted), so probe once with no override to pick the default.
    try {
      const probe = convertGpx(sourceText.value);
      outputType.value = probe.outputType;
    } catch {
      // runConversion will surface the real error below.
    }
    runConversion();
  };
  reader.onerror = () => {
    phase.value = 'error';
    errorMessage.value = t('garmin_gpx.converter.could_not_read_file');
  };
  reader.readAsText(file);
};

const onFileChange = (e: Event): void => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) loadFile(file);
};

const onDrop = (e: DragEvent): void => {
  dragging.value = false;
  const file = e.dataTransfer?.files?.[0];
  if (file) loadFile(file);
};

const setOutputType = (type: GpxOutputType): void => {
  if (outputType.value === type) return;
  outputType.value = type;
  if (sourceText.value) runConversion();
};

const download = (): void => {
  if (!result.value) return;
  const blob = new Blob([result.value.gpx], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sourceBaseName.value}-merged.gpx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const reset = (): void => {
  phase.value = 'idle';
  errorMessage.value = '';
  fileName.value = '';
  sourceText.value = '';
  result.value = null;
  if (fileInput.value) fileInput.value.value = '';
};
</script>

<style scoped lang="scss">
.gpx-tool {
  max-width: 720px;
  margin: 0 auto;
}

.gpx-dropzone {
  display: block;
  width: 100%;
  border: 2px dashed #c4ccd4;
  border-radius: 14px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  cursor: pointer;
  background: #f8fafc;
  // The page sits on a dark theme, so text would otherwise inherit a light
  // colour and vanish against this light dropzone. Pin readable dark text here.
  color: #1f2933;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #1f7a4d;
    background: #f1f7f3;
  }

  &--active {
    border-color: #1f7a4d;
    background: #e9f4ee;
  }

  &--error {
    border-color: #dc3545;
  }

  &__input {
    display: none;
  }

  &__icon {
    font-size: 48px;
    color: #1f7a4d;
  }

  &__title {
    font-size: 1.1rem;
    color: #1f2933;
  }

  // .text-muted from the global theme is tuned for the dark background and is
  // unreadable on this light dropzone, so override it with a darker grey.
  &__hint {
    font-size: 0.85rem;
    color: #52606d !important;
  }

  &__file {
    font-size: 0.9rem;
    word-break: break-all;
    color: #1f2933;
  }
}

// Bootstrap's .form-text default is too dark to read on the dark result card,
// so lift the helper text to a readable light grey.
.gpx-help {
  color: #adb5bd !important;
}
</style>
